import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  TableLayoutType,
} from "docx";
import { saveAs } from "file-saver";
import html2pdf from "html2pdf.js";
import juice from "juice";
import { toast } from "react-toastify";
const PAGE_WIDTH_DXA = 8500;
// Convert base64 to Uint8Array for docx
const base64ToUint8Array = (base64) => {
  // Remove data URL prefix if present
  const base64Data = base64.replace(/^data:image\/\w+;base64,/, "");
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// Fetch image and convert to Uint8Array
const fetchImageAsUint8Array = async (src) => {
  try {
    if (src.startsWith("data:")) {
      return base64ToUint8Array(src);
    }
    const response = await fetch(src);
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (error) {
    console.error("Error fetching image:", error);
    return null;
  }
};

const svgToPng = async (svgUrl, width = 400, height = 400) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(async (blob) => {
        const buffer = await blob.arrayBuffer();
        resolve(new Uint8Array(buffer));
      });
    };
    img.onerror = reject;
    img.src = svgUrl;
  });

const rgbToHex1 = (color) => {
  if (!color) return undefined;
  color = color.trim();
  if (color.startsWith("#")) return color.replace("#", "").toLowerCase();
  const match = color.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/i);
  if (match) {
    return (
      parseInt(match[1]).toString(16).padStart(2, "0") +
      parseInt(match[2]).toString(16).padStart(2, "0") +
      parseInt(match[3]).toString(16).padStart(2, "0")
    );
  }
  return undefined;
};

// Parse inline style string into object
const parseStyle = (styleString) => {
  const style = {};
  styleString.split(";").forEach((rule) => {
    const [key, value] = rule.split(":");
    if (key && value) style[key.trim().toLowerCase()] = value.trim();
  });
  return style;
};

// Create a TextRun from a node recursively
const createTextRun = (node, parentCss = {}) => {
  if (!node) return null;

  // Merge parent styles
  let css = { ...parentCss };

  if (node.nodeType === Node.ELEMENT_NODE) {
    const style = node.getAttribute("style") || "";
    const inlineCss = parseStyle(style);
    css = { ...css, ...inlineCss };

    // Detect tag-based formatting
    const tag = node.tagName.toUpperCase();
    if (tag === "STRONG" || tag === "B") css["font-weight"] = "bold";
    if (tag === "EM" || tag === "I") css["font-style"] = "italic";
    if (tag === "U") css["text-decoration"] = "underline";

    // Recursively process children
    return Array.from(node.childNodes)
      .map((child) => createTextRun(child, css))
      .flat();
  }

  // Text node
  if (node.nodeType === Node.TEXT_NODE) {
    if (!node.textContent.trim()) return null;
    return new TextRun({
      text: node.textContent,
      bold:
        css["font-weight"] === "bold" || parseInt(css["font-weight"]) >= 700,
      italics: css["font-style"] === "italic",
      underline: css["text-decoration"]?.includes("underline"),
      color: rgbToHex1(css["color"]),
      size: css["font-size"] ? parseInt(css["font-size"]) * 2 : 24, // half-points
      font: css["font-family"]
        ? css["font-family"].replace(/['"]/g, "")
        : undefined,
    });
  }

  return null;
};

const isLayoutTable = (tableNode) => {
  return (
    tableNode.classList.contains("header-table") ||
    tableNode.querySelector("img") ||
    tableNode.querySelector("td[colspan]")
  );
};

const buildLayoutTable = async (tableNode) => {
  const htmlRows = Array.from(tableNode.querySelectorAll("tr"));

  const rows = [];
  const columnWidths = [];

  for (const row of htmlRows) {
    const cells = Array.from(row.querySelectorAll("td, th"));
    const tableCells = [];

    for (const cell of cells) {
      const runs = [];

      for (const child of cell.childNodes) {
        if (child.tagName?.toLowerCase() === "img") {
          const src = child.getAttribute("src");

          let data;

          if (src?.endsWith(".svg")) {
            data = await svgToPng(
              src,
              parseInt(child.width) || 80,
              parseInt(child.height) || 80,
            );
          } else {
            data = await fetchImageAsUint8Array(src);
          }

          if (data) {
            runs.push(
              new ImageRun({
                data,
                transformation: {
                  width: parseInt(child.style.width) || 80,
                  height: parseInt(child.style.width) || 80,
                },
              }),
            );
          }
        } else {
          const textRuns = createTextRun(child);
          if (textRuns) {
            if (Array.isArray(textRuns)) runs.push(...textRuns);
            else runs.push(textRuns);
          }
        }
      }

      const styleAttr = cell.getAttribute("style") || "";
      const match = styleAttr.match(/width\s*:\s*(\d+)%/i);
      const widthPercent = match ? parseInt(match[1]) : null;

      const widthValue = widthPercent
        ? Math.floor((PAGE_WIDTH_DXA * widthPercent) / 100)
        : undefined;
      if (rows.length === 0 && widthValue) {
        columnWidths.push(widthValue);
      }

      tableCells.push(
        new TableCell({
          columnSpan: parseInt(cell.getAttribute("colspan") || "1"),
          width: widthValue
            ? { size: widthValue, type: WidthType.PERCENTAGE }
            : undefined,
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: runs.length ? runs : [new TextRun("")],
            }),
          ],
        }),
      );
    }

    rows.push(new TableRow({ children: tableCells }));
  }

  return new Table({
    width: { size: PAGE_WIDTH_DXA, type: WidthType.DXA },
    columnWidths,
    layout: TableLayoutType.FIXED,
    rows,
    borders: {
      top: { size: 0, color: "FFFFFF" },
      bottom: { size: 0, color: "FFFFFF" },
      left: { size: 0, color: "FFFFFF" },
      right: { size: 0, color: "FFFFFF" },
      insideHorizontal: { size: 0, color: "FFFFFF" },
      insideVertical: { size: 0, color: "FFFFFF" },
    },
  });
};

const buildDocxTable = async (tableNode) => {
  const htmlRows = Array.from(tableNode.querySelectorAll("tr"));

  if (!htmlRows.length) return null;

  let colCount = 0;
  const firstRowCells = htmlRows[0].querySelectorAll("td, th");

  firstRowCells.forEach((cell) => {
    colCount += parseInt(cell.getAttribute("colspan") || "1");
  });

  const colWidth = Math.floor(PAGE_WIDTH_DXA / colCount);

  const rows = [];

  for (const row of htmlRows) {
    const cells = Array.from(row.querySelectorAll("td, th"));

    const tableCells = [];

    for (const cell of cells) {
      const runs = [];

      for (const child of cell.childNodes) {
        if (child.tagName?.toLowerCase() === "img") {
          const src = child.getAttribute("src");

          let data;

          if (src?.endsWith(".svg")) {
            data = await svgToPng(src, 300, 200);
          } else {
            data = await fetchImageAsUint8Array(src);
          }

          if (data) {
            runs.push(
              new ImageRun({
                data,
                transformation: {
                  width: 200,
                  height: 150,
                },
              }),
            );
          }
        } else {
          const textRuns = createTextRun(child);

          if (textRuns) {
            if (Array.isArray(textRuns)) runs.push(...textRuns);
            else runs.push(textRuns);
          }
        }
      }

      tableCells.push(
        new TableCell({
          width: { size: colWidth, type: WidthType.DXA },
          children: [
            new Paragraph({
              children: runs.length ? runs : [new TextRun("")],
            }),
          ],
        }),
      );
    }

    rows.push(new TableRow({ children: tableCells }));
  }

  return new Table({
    width: {
      size: PAGE_WIDTH_DXA,
      type: WidthType.DXA,
    },
    columnWidths: Array(colCount).fill(colWidth),
    layout: TableLayoutType.FIXED,
    rows,
  });
};

// Convert HTML node to Paragraph(s)
const createParagraphOrTable = async (node, parentCss = {}) => {
  const tag = node.tagName?.toLowerCase();

  if (["h1", "h2", "h3"].includes(tag)) {
    return new Paragraph({
      text: node.textContent,
      heading:
        tag === "h1"
          ? HeadingLevel.HEADING_1
          : tag === "h2"
            ? HeadingLevel.HEADING_2
            : HeadingLevel.HEADING_3,
      spacing: { before: 240, after: 120 },
    });
  }

  if (tag === "p") {
    return new Paragraph({
      children: Array.from(node.childNodes)
        .map((child) => createTextRun(child, parentCss))
        .flat()
        .filter(Boolean),
      spacing: { before: 120, after: 120 },
    });
  }

  if (tag === "ul" || tag === "ol") {
    const paragraphs = [];

    const processList = async (listEl, level = 0) => {
      const isOrdered = listEl.tagName.toLowerCase() === "ol";

      // Detect pseudo-nested by margin-left
      const styleMargin = parseInt(listEl.style.marginLeft || "0");
      const pseudoLevel = styleMargin ? Math.floor(styleMargin / 20) : 0;
      const currentLevel = Math.max(level, pseudoLevel);

      for (const li of Array.from(listEl.children)) {
        if (li.tagName.toLowerCase() !== "li") continue;

        const runs = Array.from(li.childNodes)
          .filter(
            (n) =>
              n.tagName?.toLowerCase() !== "ul" &&
              n.tagName?.toLowerCase() !== "ol",
          )
          .map((child) => createTextRun(child, parentCss))
          .flat()
          .filter(Boolean);

        if (runs.length > 0) {
          paragraphs.push(
            new Paragraph({
              children: runs,
              numbering: isOrdered
                ? { reference: "decimal-list", level: currentLevel }
                : undefined,
              bullet: !isOrdered ? { level: currentLevel } : undefined,
              spacing: { before: 80, after: 80 },
            }),
          );
        }

        // Check nested <ul>/<ol> inside this <li>
        for (const nested of Array.from(li.children)) {
          if (
            nested.tagName.toLowerCase() === "ul" ||
            nested.tagName.toLowerCase() === "ol"
          ) {
            await processList(nested, currentLevel + 1);
          }
        }
      }
    };

    await processList(node, 0);
    return paragraphs;
  }

  if (tag === "img") {
    const src = node.getAttribute("src");
    if (!src) return null; // skip if no src

    let data;
    if (src.endsWith(".svg")) {
      // Use a PNG version if external
      if (src.startsWith("http")) {
        // Replace with PNG URL (Wikipedia has PNG thumbnails)
        const pngUrl =
          src.replace(".svg", ".png").replace("/commons/", "/commons/thumb/") +
          "/1200px-" +
          src.split("/").pop().replace(".svg", ".svg.png");
        data = await fetchImageAsUint8Array(pngUrl);
      } else {
        // Local/base64 SVGs: convert to PNG
        data = await svgToPng(
          src,
          parseInt(node.width) || 400,
          parseInt(node.height) || 300,
        );
      }
    } else {
      data = await fetchImageAsUint8Array(src);
    }

    const alignment =
      node.style.float === "right"
        ? AlignmentType.RIGHT
        : node.style.float === "left"
          ? AlignmentType.LEFT
          : AlignmentType.CENTER;

    return new Paragraph({
      alignment,
      children: [
        new ImageRun({
          data,
          transformation: {
            width: parseInt(node.width) || 400,
            height: parseInt(node.height) || 300,
          },
        }),
      ],
      spacing: { before: 120, after: 120 },
    });
  }

  if (tag === "table") {
    if (isLayoutTable(node)) {
      return await buildLayoutTable(node);
    }

    const table = await buildDocxTable(node);
    if (table) return table;

    return null;
  }

  // Recursively process child nodes
  const children = [];
  node.childNodes.forEach((child) => {
    if (child.nodeType === 1 || child.nodeType === 3)
      children.push(createParagraphOrTable(child, parentCss));
  });

  return (await Promise.all(children)).flat();
};

// Main function to download DOCX
export const downloadDocx1 = async (
  htmlContent,
  filename = "document.docx",
) => {
  // Step 1: Inline CSS
  const inlinedHtml = juice(htmlContent, {
    applyStyleTags: true,
    removeStyleTags: false,
    preserveImportant: true,
  });

  // Step 2: Parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(inlinedHtml, "text/html");

  // Step 3: Convert to paragraphs
  const paragraphs = (
    await Promise.all(
      Array.from(doc.body.childNodes).map(createParagraphOrTable),
    )
  ).flat();

  // Step 4: Build DOCX
  const docx = new Document({
    numbering: {
      config: [
        {
          reference: "decimal-list", // numbered list 1,2,3
          levels: [
            {
              level: 0,
              format: "decimal",
              text: "%1.",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
            {
              level: 1,
              format: "decimal",
              text: "%1.%2.",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 1440, hanging: 360 } } },
            },
          ],
        },
        {
          reference: "lettered-list", // lettered list a,b,c
          levels: [
            {
              level: 0,
              format: "lowerLetter",
              text: "%1.",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
            {
              level: 1,
              format: "decimal",
              text: "%1.%2.",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 1440, hanging: 360 } } },
            },
          ],
        },
      ],
    },
    sections: [{ children: paragraphs }],
  });

  const blob = await Packer.toBlob(docx);
  saveAs(blob, filename);
};

export const downloadPdf = async (html, filename = "document.pdf") => {
  // Create a container with inlined styles
  const inlinedHtml = juice(html);

  const container = document.createElement("div");
  container.innerHTML = inlinedHtml;

  // ✅ Add this right after setting innerHTML
  container.querySelectorAll("ol").forEach((ol) => {
    if (!ol.hasAttribute("type")) ol.setAttribute("type", "1");
  });

  // container.style.padding = "20px";
  // container.style.width = "210mm";
  container.style.boxSizing = "border-box";
  container.style.width = "100%";
  container.style.padding = "0";
  container.style.paddingBottom = "15mm";

  container.querySelectorAll("*").forEach((el) => {
    const style = el.style;
    style.boxSizing = "border-box";
    style.clear = "both";
    style.overflow = "visible";
    style.pageBreakInside = "avoid"; // avoid cutting content in PDF
    style.lineHeight = style.lineHeight || "1.6";

    // Images: preserve size and avoid breaking
    // if (el.tagName.toLowerCase() === "img") {
    //   el.style.maxWidth = "100%";
    //   el.style.height = "auto";
    //   el.style.pageBreakInside = "avoid";
    //   el.style.breakInside = "avoid";
    //   el.crossOrigin = "anonymous"; // won't fetch, but safe for html2canvas
    // }

    if (!style.marginBottom) {
      style.marginBottom = "25px";
    }
  });

  const style = document.createElement("style");
  style.textContent = `
    * {
      word-break: break-word;
      overflow-wrap: anywhere;
      max-width: 100%;
    }
    /* Prevent breaks inside all block elements */
    .section, .slide, .container > *, div:not(.container), p, h1, h2, h3, h4, ul, ol, li, table, tr, blockquote {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    
    /* Keep headings with their following content */
    h1, h2, h3, h4 {
      page-break-after: avoid !important;
      break-after: avoid !important;
    }
    
    /* Keep intro paragraph with the first section */
    .container > p:first-of-type {
      page-break-after: avoid !important;
      break-after: avoid !important;
    }
    
    /* Orphan/widow control */
    p, li {
      orphans: 4;
      widows: 4;
    }
    
    /* Add spacing to help with page breaks */
    .section {
      padding-top: 10px;
      padding-bottom: 10px;
    }
    
    /* Image handling - preserve sizes and alignment */
    img {
      max-width: 100% !important;
      height: auto !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    
    /* Respect inline image dimensions */
    img[style*="width"] {
      max-width: none !important;
    }

  /* ===== FIX preformatted blocks (math / formulas) ===== */
  pre {
    white-space: pre-wrap !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    overflow-x: hidden !important;

    max-width: 100% !important;
    box-sizing: border-box !important;

    font-family: "Courier New", Courier, monospace;
    font-size: 12px;
    line-height: 1.4;

    padding: 8px;
    background-color: #e9ecef;
    border-radius: 4px;
  }

  /* ===== FIX inline code ===== */
  code {
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;

    font-family: "Courier New", Courier, monospace;
    font-size: 0.95em;
    background-color: transparent !important;
  }

  /* Prevent awkward page splits */
  pre, code {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  ul {
  list-style-type: decimal !important; /* bullets for unordered lists */
  }

  ol[type="1"] {
    list-style-type: decimal !important; /* numbers */
  }

  ol[type="a"] {
    list-style-type: lower-alpha !important; /* letters */
  }

  li {
    display: list-item !important;
    margin-bottom: 4px;
  }
  `;
  container.prepend(style);

  const opt = {
    margin: [10, 10, 12, 10],
    filename,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      // crossOrigin: "anonymous",

      letterRendering: true,
      logging: false,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    },
    pagebreak: {
      mode: ["avoid-all", "css", "legacy"],
      before: ".page-break-before",
      after: ".page-break-after",
      avoid:
        ".section, .slide, .container > p, h1, h2, h3, h4, ul, ol, table, blockquote, li",
    },
  };

  try {
    await html2pdf().set(opt).from(container).save();
  } catch (error) {
    console.error("Error exporting PDF:", error);
    throw error;
  }
};

export const exportDocument = (content, title, type) => {
  if (!content) {
    toast.error("Nothing to export");
    return;
  }

  switch (type) {
    case "pdf":
      downloadPdf(content, title);
      break;
    case "docx":
      downloadDocx1(content, title);
      break;
    default:
      toast.error("Unsupported export format");
      return;
  }

  toast.success("File downloaded!");
};

// export const downloadDocx = async (html, filename = "document.docx") => {
//   try {
//     const paragraphs = await parseHtmlToDocxElements(html);

//     const doc = new Document({
//       sections: [
//         {
//           properties: {},
//           children:
//             paragraphs.length > 0
//               ? paragraphs
//               : [new Paragraph({ children: [new TextRun("Empty document")] })],
//         },
//       ],
//     });

//     const blob = await Packer.toBlob(doc);
//     saveAs(blob, filename);
//   } catch (error) {
//     console.error("Error exporting DOCX:", error);
//     throw error;
//   }
// };

// const parseHtmlToDocxElements = async (html) => {
//   const inlinedHtml = juice(html);
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(inlinedHtml, "text/html");
//   const elements = [];
//   const imagePromises = [];

//   const processNode = (node, inheritedStyles = {}) => {
//     if (node.nodeType === Node.TEXT_NODE) {
//       const text = node.textContent?.trim();
//       if (text) {
//         const hexColor = rgbToHex(inheritedStyles.color);
//         return [
//           new TextRun({
//             text,
//             bold:
//               inheritedStyles.fontWeight === "bold" ||
//               inheritedStyles.fontWeight === "700",
//             italics: inheritedStyles.fontStyle === "italic",
//             color: hexColor,
//           }),
//         ];
//       }
//       return [];
//     }

//     if (node.nodeType !== Node.ELEMENT_NODE) return [];

//     const element = node;
//     const tagName = element.tagName.toLowerCase();
//     const computedStyle = element.style;

//     const styles = { ...inheritedStyles };
//     if (computedStyle.fontWeight) styles.fontWeight = computedStyle.fontWeight;
//     if (computedStyle.fontStyle) styles.fontStyle = computedStyle.fontStyle;
//     if (computedStyle.color) styles.color = computedStyle.color;

//     // Handle different HTML elements
//     switch (tagName) {
//       case "img": {
//         const src = element.getAttribute("src");
//         if (src) {
//           // Get dimensions from inline styles or attributes
//           let width =
//             parseInt(computedStyle.width) ||
//             parseInt(element.getAttribute("width")) ||
//             300;
//           let height =
//             parseInt(computedStyle.height) ||
//             parseInt(element.getAttribute("height")) ||
//             200;

//           // Get alignment from style or parent
//           const align =
//             computedStyle.float === "right"
//               ? AlignmentType.RIGHT
//               : computedStyle.float === "left"
//                 ? AlignmentType.LEFT
//                 : computedStyle.textAlign === "center"
//                   ? AlignmentType.CENTER
//                   : AlignmentType.LEFT;

//           // Create a placeholder index for the image
//           const placeholderIndex = elements.length;
//           elements.push(null); // Placeholder

//           // Add promise to load image
//           imagePromises.push(
//             (async () => {
//               const imageData = await fetchImageAsUint8Array(src);
//               if (imageData) {
//                 // Get actual dimensions if not specified
//                 const dimensions = await getImageDimensions(src);
//                 const finalWidth = width || dimensions.width;
//                 const finalHeight = height || dimensions.height;

//                 // Scale down if too large (max width ~600px in document)
//                 const maxWidth = 600;
//                 let scaledWidth = finalWidth;
//                 let scaledHeight = finalHeight;
//                 if (scaledWidth > maxWidth) {
//                   const scale = maxWidth / scaledWidth;
//                   scaledWidth = maxWidth;
//                   scaledHeight = Math.round(scaledHeight * scale);
//                 }

//                 elements[placeholderIndex] = new Paragraph({
//                   alignment: align,
//                   children: [
//                     new ImageRun({
//                       data: imageData,
//                       transformation: {
//                         width: scaledWidth,
//                         height: scaledHeight,
//                       },
//                     }),
//                   ],
//                   spacing: { before: 100, after: 100 },
//                 });
//               } else {
//                 // Remove placeholder if image failed
//                 elements[placeholderIndex] = new Paragraph({
//                   children: [
//                     new TextRun({
//                       text: "[Image could not be loaded]",
//                       italics: true,
//                     }),
//                   ],
//                 });
//               }
//             })(),
//           );
//         }
//         break;
//       }
//       case "h1":
//         elements.push(
//           new Paragraph({
//             heading: HeadingLevel.HEADING_1,
//             children: Array.from(element.childNodes).flatMap((child) =>
//               processNode(child, { ...styles, fontWeight: "bold" }),
//             ),
//             spacing: { before: 400, after: 200 },
//           }),
//         );
//         break;
//       case "h2":
//         elements.push(
//           new Paragraph({
//             heading: HeadingLevel.HEADING_2,
//             children: Array.from(element.childNodes).flatMap((child) =>
//               processNode(child, { ...styles, fontWeight: "bold" }),
//             ),
//             spacing: { before: 300, after: 150 },
//           }),
//         );
//         break;
//       case "h3":
//         elements.push(
//           new Paragraph({
//             heading: HeadingLevel.HEADING_3,
//             children: Array.from(element.childNodes).flatMap((child) =>
//               processNode(child, { ...styles, fontWeight: "bold" }),
//             ),
//             spacing: { before: 200, after: 100 },
//           }),
//         );
//         break;
//       case "p":
//         const alignment =
//           computedStyle.textAlign === "center"
//             ? AlignmentType.CENTER
//             : computedStyle.textAlign === "right"
//               ? AlignmentType.RIGHT
//               : AlignmentType.LEFT;
//         elements.push(
//           new Paragraph({
//             children: Array.from(element.childNodes).flatMap((child) =>
//               processNode(child, styles),
//             ),
//             spacing: { before: 100, after: 100 },
//             alignment,
//           }),
//         );
//         break;
//       case "ul":
//       case "ol":
//         Array.from(element.children).forEach((li, index) => {
//           if (li.tagName.toLowerCase() === "li") {
//             const bullet = tagName === "ul" ? "• " : `${index + 1}. `;
//             elements.push(
//               new Paragraph({
//                 children: [
//                   new TextRun({ text: bullet }),
//                   ...Array.from(li.childNodes).flatMap((child) =>
//                     processNode(child, styles),
//                   ),
//                 ],
//                 spacing: { before: 50, after: 50 },
//                 indent: { left: 720 },
//               }),
//             );
//           }
//         });
//         break;
//       case "strong":
//       case "b":
//         return Array.from(element.childNodes).flatMap((child) =>
//           processNode(child, { ...styles, fontWeight: "bold" }),
//         );
//       case "em":
//       case "i":
//         return Array.from(element.childNodes).flatMap((child) =>
//           processNode(child, { ...styles, fontStyle: "italic" }),
//         );
//       case "div":
//       case "section":
//       case "article":
//       case "span":
//         Array.from(element.childNodes).forEach((child) =>
//           processNode(child, styles),
//         );
//         break;
//       case "br":
//         elements.push(new Paragraph({ children: [] }));
//         break;
//       default:
//         Array.from(element.childNodes).forEach((child) =>
//           processNode(child, styles),
//         );
//     }

//     return [];
//   };

//   processNode(doc.body);
//   await Promise.all(imagePromises);

//   // Filter out any remaining null placeholders
//   return elements.filter((el) => el !== null);
// };
